import { Slot } from 'radix-ui';
import {
  cloneElement,
  type ComponentProps,
  type CSSProperties,
  isValidElement,
  type ReactNode,
} from 'react';

import { tv } from '../../lib/tv';

/** Figma "Tone": what kind of event it is. */
export type EventChipTone =
  'brand' | 'info' | 'success' | 'warning' | 'danger' | 'neutral';

/** Height per hour of duration (Figma: 56px per hour). */
export const EVENT_HOUR_HEIGHT = 56;

export type EventChipProps = Omit<ComponentProps<'div'>, 'title'> & {
  /** Brand = production, Info = meetings, Success = published, Warning = review, Danger = deadline, Neutral = personal. */
  tone?: EventChipTone;
  /** Event title - always shown, so the tone is never the only cue. */
  title: ReactNode;
  /** Time line, e.g. "09:30 – 10:30". */
  time?: ReactNode;
  /**
   * Length in minutes: sets the height (56px per hour) and hides the time
   * line at 30 minutes or less. Without it the chip hugs its content.
   */
  duration?: number;
  /** Render the single child (a button or link opening the event) as the chip. */
  asChild?: boolean;
};

const eventChip = tv({
  slots: {
    root: 'relative flex min-w-0 overflow-hidden rounded-sm text-left outline-none focus-visible:shadow-focus-ring',
    bar: 'w-[3px] shrink-0 self-stretch',
    text: 'flex min-w-0 flex-1 flex-col px-2 py-1.5',
    title: 'text-label-sm text-text-primary truncate',
    time: 'text-caption truncate',
  },
  variants: {
    tone: {
      brand: { root: 'bg-brand-subtle', bar: 'bg-brand-strong', time: 'text-text-brand' },
      info: {
        root: 'bg-status-info-bg',
        bar: 'bg-status-info-solid',
        time: 'text-status-info-fg',
      },
      success: {
        root: 'bg-status-success-bg',
        bar: 'bg-status-success-solid',
        time: 'text-status-success-fg',
      },
      warning: {
        root: 'bg-status-warning-bg',
        bar: 'bg-status-warning-solid',
        time: 'text-status-warning-fg',
      },
      danger: {
        root: 'bg-status-danger-bg',
        bar: 'bg-status-danger-solid',
        time: 'text-status-danger-fg',
      },
      neutral: {
        root: 'bg-bg-muted',
        bar: 'bg-border-strong',
        time: 'text-text-secondary',
      },
    },
  },
  defaultVariants: { tone: 'brand' },
});

/**
 * A calendar event (Figma: Calendar ↳ Event Chip, 63:198): tone fill with a
 * 3px bar on the left, Label/Small title and a Caption time line in the
 * tone's foreground. Place it in a day / timeline column; `duration` sizes
 * it. With `asChild`, wrap a button or link to make it open the event.
 */
export function EventChip({
  tone,
  title,
  time,
  duration,
  asChild = false,
  className,
  style,
  children,
  ...props
}: EventChipProps) {
  const styles = eventChip({ tone });
  const showTime = time !== undefined && (duration === undefined || duration > 30);
  const height: CSSProperties | undefined =
    duration === undefined ? undefined : { height: (duration / 60) * EVENT_HOUR_HEIGHT };
  const content = (
    <>
      <span aria-hidden data-slot="event-chip-bar" className={styles.bar()} />
      <span className={styles.text()}>
        <span data-slot="event-chip-title" className={styles.title()}>
          {title}
        </span>
        {showTime && (
          <span data-slot="event-chip-time" className={styles.time()}>
            {time}
          </span>
        )}
      </span>
    </>
  );
  const child =
    asChild && isValidElement<{ children?: ReactNode }>(children) ? children : null;
  const Comp = asChild ? Slot.Root : 'div';
  return (
    <Comp
      data-slot="event-chip"
      data-tone={tone ?? 'brand'}
      className={styles.root({ className })}
      style={{ ...height, ...style }}
      {...props}
    >
      {child ? (
        <Slot.Slottable>{cloneElement(child, undefined, content)}</Slot.Slottable>
      ) : (
        content
      )}
    </Comp>
  );
}
